/**
 * Tenant Form Component
 *
 * Form for creating and editing tenants.
 *
 * @fileoverview Tenant form component
 */

import React, { useState, useEffect } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Button,
  Box,
  Heading,
  Divider,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import type { AdminTenant, CreateTenantInput, UpdateTenantInput } from '../../types/admin';
import { formatPhoneE164 } from '../../utils/validation';

interface TenantFormProps {
  tenant?: AdminTenant | null;
  loading: boolean;
  error: string | null;
  onSubmit: (data: CreateTenantInput | UpdateTenantInput) => void | Promise<void>;
  onCancel: () => void;
}

interface FormErrors {
  name?: string;
  slug?: string;
  ein?: string;
  phoneNumber?: string;
  website?: string;
  managerEmail?: string;
}

/**
 * Tenant Form Component
 *
 * Handles creation and editing of tenants.
 */
export const TenantForm: React.FC<TenantFormProps> = ({
  tenant,
  loading,
  error,
  onSubmit,
  onCancel,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    ein: '',
    address: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    phoneNumber: '',
    website: '',
    managerName: '',
    managerEmail: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Initialize form with tenant data
  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        slug: tenant.slug || '',
        ein: tenant.ein || '',
        address: tenant.address || '',
        addressLine2: tenant.addressLine2 || '',
        city: tenant.city || '',
        state: tenant.state || '',
        zipCode: tenant.zipCode || '',
        country: tenant.country || 'US',
        phoneNumber: tenant.phoneNumber || '',
        website: tenant.website || '',
        managerName: tenant.managerName || '',
        managerEmail: tenant.managerEmail || '',
      });
    }
  }, [tenant]);

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.length < 2) return 'Name must be at least 2 characters';
        break;
      case 'slug':
        if (value && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
          return 'Slug must be lowercase, alphanumeric with hyphens only';
        }
        break;
      case 'ein':
        if (value && !/^\d{2}-\d{7}$/.test(value)) {
          return 'EIN must be in format XX-XXXXXXX';
        }
        break;
      case 'phoneNumber': {
        const digits = value.replace(/\D/g, '');
        const hasPlus = value.trim().startsWith('+');
        const validUS = !hasPlus && digits.length === 10;
        const validUS11 = digits.length === 11 && digits.startsWith('1');
        const validE164 = hasPlus && /^\+[1-9]\d{1,14}$/.test(value.replace(/\s/g, ''));
        if (value && !validUS && !validUS11 && !validE164) {
          return 'Enter a 10-digit US number (e.g., 555 123 4567) or international format (e.g., +44 20 1234 5678)';
        }
        break;
      }
      case 'website':
        if (value && !/^https?:\/\/.+/.test(value)) {
          return 'Website must be a valid URL';
        }
        break;
      case 'managerEmail':
        if (!isEditing && !value.trim()) return 'Manager email is required';
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Must be a valid email address';
        }
        break;
    }
    return undefined;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key as keyof FormErrors] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Build submission data
    const data: CreateTenantInput | UpdateTenantInput = {
      name: formData.name,
      ...(formData.slug && { slug: formData.slug }),
      ...(formData.ein && { ein: formData.ein }),
      ...(formData.address && { address: formData.address }),
      ...(formData.addressLine2 && { addressLine2: formData.addressLine2 }),
      ...(formData.city && { city: formData.city }),
      ...(formData.state && { state: formData.state }),
      ...(formData.zipCode && { zipCode: formData.zipCode }),
      ...(formData.country && { country: formData.country }),
      ...(formData.phoneNumber && { phoneNumber: formatPhoneE164(formData.phoneNumber) }),
      ...(formData.website && { website: formData.website }),
      ...(formData.managerName && { managerName: formData.managerName }),
      ...(formData.managerEmail && { managerEmail: formData.managerEmail }),
    };

    onSubmit(data);
  };

  const isEditing = !!tenant;

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={bgColor}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      p={6}
    >
      <VStack spacing={6} align="stretch">
        {/* Basic Info */}
        <Box>
          <Heading size="sm" mb={4}>Basic Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired isInvalid={touched.name && !!errors.name}>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Acme Corporation"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={touched.slug && !!errors.slug}>
              <FormLabel>Slug</FormLabel>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="acme-corp"
              />
              <FormHelperText>
                {isEditing ? 'URL-friendly identifier' : 'Auto-generated if not provided'}
              </FormHelperText>
              <FormErrorMessage>{errors.slug}</FormErrorMessage>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Divider />

        {/* Business Info */}
        <Box>
          <Heading size="sm" mb={4}>Business Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isInvalid={touched.ein && !!errors.ein}>
              <FormLabel>EIN</FormLabel>
              <Input
                name="ein"
                value={formData.ein}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="12-3456789"
              />
              <FormErrorMessage>{errors.ein}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={touched.phoneNumber && !!errors.phoneNumber}>
              <FormLabel>Phone Number</FormLabel>
              <Input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="555 123 4567"
              />
              <FormErrorMessage>{errors.phoneNumber}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={touched.website && !!errors.website}>
              <FormLabel>Website</FormLabel>
              <Input
                name="website"
                value={formData.website}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="https://acme.com"
              />
              <FormErrorMessage>{errors.website}</FormErrorMessage>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Divider />

        {/* Address */}
        <Box>
          <Heading size="sm" mb={4}>Address</Heading>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Street Address</FormLabel>
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Address Line 2</FormLabel>
              <Input
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleChange}
                placeholder="Suite 100"
              />
            </FormControl>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full">
              <FormControl>
                <FormLabel>City</FormLabel>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="New York"
                />
              </FormControl>

              <FormControl>
                <FormLabel>State</FormLabel>
                <Input
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="NY"
                />
              </FormControl>

              <FormControl>
                <FormLabel>ZIP Code</FormLabel>
                <Input
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  placeholder="10001"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Country</FormLabel>
                <Input
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="US"
                />
              </FormControl>
            </SimpleGrid>
          </VStack>
        </Box>

        <Divider />

        {/* Manager Info */}
        <Box>
          <Heading size="sm" mb={4}>Manager Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Manager Name</FormLabel>
              <Input
                name="managerName"
                value={formData.managerName}
                onChange={handleChange}
                placeholder="John Smith"
              />
            </FormControl>

            <FormControl isRequired={!isEditing} isInvalid={touched.managerEmail && !!errors.managerEmail}>
              <FormLabel>Manager Email</FormLabel>
              <Input
                name="managerEmail"
                type="email"
                value={formData.managerEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="john@acme.com"
              />
              <FormErrorMessage>{errors.managerEmail}</FormErrorMessage>
            </FormControl>
          </SimpleGrid>
        </Box>

        {/* Error message */}
        {error && (
          <Box color="red.500" fontSize="sm">
            {error}
          </Box>
        )}

        {/* Actions */}
        <Box pt={4} display="flex" gap={4} justifyContent="flex-end">
          <Button variant="ghost" onClick={onCancel} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={loading}
            loadingText={isEditing ? 'Saving...' : 'Creating...'}
          >
            {isEditing ? 'Save Changes' : 'Create Tenant'}
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

export default TenantForm;
